import {
  Bot,
  Database,
  ExternalLink,
  FolderOpen,
  Info,
  Link,
  Loader2,
  MessageSquare,
  PlusCircle,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EditAgentModal } from "@/components/agents/EditAgentModal";
import { getResourceIcon, timeAgo } from "@/components/project-hub/utils";
import { useToast } from "@/components/shared";
import { useApp } from "@/context/AppContext";
import { buildChatSessionPath } from "@/lib/chatNavigation";
import { agentService, dataSourceService, projectService, skillService, toolService } from "@/lib/services";
import type {
  Agent,
  AgentUpdate,
  DataCollection,
  ProjectHub as ProjectHubType,
  ProjectResource,
  SkillSummary,
  ToolSummary,
} from "@/types";

// ============================================================================
// RESOURCES TAB (old Overview tab, kept for detailed resource management)
// ============================================================================

export function ResourcesTab({
  hub,
  navigate,
  canEdit,
  onRemoveResource,
  onRefresh,
}: Readonly<{
  hub: ProjectHubType;
  navigate: ReturnType<typeof useNavigate>;
  canEdit: boolean;
  onRemoveResource: (uuid: string) => void;
  onRefresh: () => Promise<void> | void;
}>) {
  const { enabledModels, tierDefaults } = useApp();
  const { showToast } = useToast();
  const agents = hub.resources.filter((r) => r.resourceType === "agent");
  const dataSources = hub.resources.filter((r) => r.resourceType === "data_collection");
  const chats = hub.resources.filter((r) => r.resourceType === "chat_session");
  const links = hub.resources.filter((r) => r.resourceType === "link");
  const [addModalType, setAddModalType] = useState<"agent" | "data_collection" | null>(null);

  // Edit agent inline
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [availableSkills, setAvailableSkills] = useState<SkillSummary[]>([]);
  const [availableTools, setAvailableTools] = useState<ToolSummary[]>([]);
  const [allDataSources, setAllDataSources] = useState<DataCollection[]>([]);

  const openEditAgent = async (agentUuid: string) => {
    try {
      const [agentRes, skillsRes, toolsRes, dsRes] = await Promise.all([
        agentService.get(agentUuid),
        skillService.list({ pageSize: 100 }),
        toolService.list({ pageSize: 100 }),
        dataSourceService.list({ pageSize: 100 }),
      ]);
      setAvailableSkills(skillsRes.data);
      setAvailableTools(toolsRes.data);
      setAllDataSources(dsRes.data);
      setEditingAgent(agentRes.data);
    } catch (err) {
      console.error("Failed to load agent for editing:", err);
      showToast("Failed to open agent editor", "error");
    }
  };

  const handleSaveAgent = async (data: AgentUpdate) => {
    if (!editingAgent) return;
    await agentService.update(editingAgent.uuid, data);
    setEditingAgent(null);
    showToast("Agent updated", "success");
    onRefresh();
  };

  return (
    <div className="p-6 space-y-8">
      {/* Agents */}
      {(agents.length > 0 || canEdit) && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-content-primary">Agents</h2>
            {canEdit && (
              <button
                onClick={() => setAddModalType("agent")}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors"
              >
                <PlusCircle className="h-4 w-4" />
                Add Agent
              </button>
            )}
          </div>
          {agents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {agents.map((resource) => (
                <ResourceCard
                  key={resource.uuid}
                  resource={resource}
                  navigate={navigate}
                  canEdit={canEdit}
                  onRemove={onRemoveResource}
                  projectUuid={hub.uuid}
                  isDefaultAgent={resource.agentUuid === hub.defaultAgent?.uuid}
                  onEditAgent={openEditAgent}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-content-tertiary">No agents added yet.</p>
          )}
        </section>
      )}

      {/* Data Sources */}
      {(dataSources.length > 0 || canEdit) && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-content-primary">Data Sources</h2>
            {canEdit && (
              <button
                onClick={() => setAddModalType("data_collection")}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors"
              >
                <PlusCircle className="h-4 w-4" />
                Add Data Source
              </button>
            )}
          </div>
          {dataSources.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dataSources.map((resource) => (
                <ResourceCard
                  key={resource.uuid}
                  resource={resource}
                  navigate={navigate}
                  canEdit={canEdit}
                  onRemove={onRemoveResource}
                  projectUuid={hub.uuid}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-content-tertiary">No data sources added yet.</p>
          )}
        </section>
      )}

      {/* Conversations */}
      {chats.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-content-primary mb-4">Conversations</h2>
          <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
            {chats.map((resource, idx) => (
              <div
                key={resource.uuid}
                className={`flex items-center gap-4 px-5 py-3.5 hover:bg-interactive-hover transition-colors ${
                  idx < chats.length - 1 ? "border-b border-border-primary" : ""
                }`}
              >
                <button
                  onClick={() =>
                    resource.chatSessionUuid && navigate(buildChatSessionPath(resource.chatSessionUuid, hub.uuid))
                  }
                  className="flex items-center gap-4 flex-1 min-w-0 text-left"
                >
                  <div className="h-9 w-9 rounded-lg bg-brand-primary/10 flex items-center justify-center shrink-0">
                    <MessageSquare className="h-4 w-4 text-brand-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-content-primary truncate">
                      {resource.chatSessionTitle || "Untitled conversation"}
                    </p>
                    <p className="text-xs text-content-quaternary mt-0.5">
                      {resource.addedByName && `Added by ${resource.addedByName}`}
                    </p>
                  </div>
                  <span className="text-xs text-content-quaternary whitespace-nowrap shrink-0">
                    {timeAgo(resource.createdAt)}
                  </span>
                </button>
                {canEdit && (
                  <button
                    onClick={() => onRemoveResource(resource.uuid)}
                    className="p-1.5 text-content-quaternary hover:text-red-400 transition-colors"
                    title="Remove from project"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Links */}
      {links.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-content-primary mb-4">Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {links.map((resource) => (
              <div
                key={resource.uuid}
                className="bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-border-secondary transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-500/20 flex items-center justify-center shrink-0">
                    <Link className="h-5 w-5 text-purple-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <a
                      href={resource.url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-content-primary text-sm truncate hover:text-brand-primary transition-colors flex items-center gap-1"
                    >
                      {resource.title || resource.url}
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                    {resource.url && resource.title && (
                      <p className="text-xs text-content-quaternary truncate mt-1">{resource.url}</p>
                    )}
                    <p className="text-xs text-content-quaternary mt-1">
                      {resource.addedByName && `Added by ${resource.addedByName}`}
                    </p>
                  </div>
                  {canEdit && (
                    <button
                      onClick={() => onRemoveResource(resource.uuid)}
                      className="p-1.5 text-content-quaternary hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                      title="Remove from project"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Empty state — only shown to viewers when nothing exists at all;
      editors already get actionable Add buttons in the sections above. */}
      {hub.resources.length === 0 && !canEdit && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center mb-4">
            <FolderOpen className="h-8 w-8 text-brand-primary" />
          </div>
          <h3 className="text-lg font-medium text-content-primary">No resources yet</h3>
          <p className="text-content-tertiary mt-1 text-center max-w-sm">
            Add agents, data sources, conversations, and links to this project to keep everything organized.
          </p>
        </div>
      )}

      {addModalType && (
        <AddResourceModal
          resourceType={addModalType}
          projectId={hub.uuid}
          existingUuids={
            new Set(
              (addModalType === "agent" ? agents : dataSources)
                .map((r) => (addModalType === "agent" ? r.agentUuid : r.dataCollectionUuid))
                .filter((v): v is string => !!v),
            )
          }
          onClose={() => setAddModalType(null)}
          onAdded={() => onRefresh()}
        />
      )}

      {editingAgent && (
        <EditAgentModal
          agent={editingAgent}
          onClose={() => setEditingAgent(null)}
          onSave={handleSaveAgent}
          models={enabledModels}
          tierDefaults={tierDefaults}
          dataSources={allDataSources}
          availableSkills={availableSkills}
          availableTools={availableTools}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resource Card (agents & data sources)
// ---------------------------------------------------------------------------

function ResourceCard({
  resource,
  navigate,
  canEdit,
  onRemove,
  projectUuid,
  isDefaultAgent,
  onEditAgent,
}: Readonly<{
  resource: ProjectResource;
  navigate: ReturnType<typeof useNavigate>;
  canEdit: boolean;
  onRemove: (uuid: string) => void;
  projectUuid: string;
  isDefaultAgent?: boolean;
  onEditAgent?: (agentUuid: string) => void;
}>) {
  const Icon = getResourceIcon(resource.resourceType);

  const handleClick = () => {
    if (resource.resourceType === "agent" && resource.agentUuid && onEditAgent) {
      onEditAgent(resource.agentUuid);
    } else if (resource.resourceType === "data_collection" && resource.dataCollectionUuid) {
      navigate(`/data-sources/${resource.dataCollectionUuid}`);
    } else if (resource.resourceType === "chat_session" && resource.chatSessionUuid) {
      navigate(buildChatSessionPath(resource.chatSessionUuid, projectUuid));
    }
  };

  const getIconColor = () => {
    if (resource.resourceType === "agent" && resource.agentColor) {
      return resource.agentColor;
    }
    switch (resource.resourceType) {
      case "data_collection":
        return "#10b981"; // emerald
      case "chat_session":
        return "#EC622B"; // brand orange
      case "link":
        return "#a855f7"; // purple
      default:
        return "#06b6d4";
    }
  };

  const iconColor = getIconColor();
  const name =
    resource.agentName || resource.dataCollectionName || resource.chatSessionTitle || resource.title || "Untitled";

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-border-secondary transition-colors group">
      <div className="flex items-start gap-3">
        <button onClick={handleClick} className="flex items-start gap-3 flex-1 min-w-0 text-left">
          <div
            className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${iconColor}20` }}
          >
            <Icon className="h-5 w-5" style={{ color: iconColor }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-content-primary text-sm truncate group-hover:text-brand-primary transition-colors">
              {name}
            </h3>
            {resource.resourceType === "data_collection" && resource.dataCollectionSourceType && (
              <p className="text-xs text-content-quaternary mt-1 capitalize">
                {resource.dataCollectionSourceType.replace("_", " ")}
              </p>
            )}
            <p className="text-xs text-content-quaternary mt-1">
              {resource.addedByName && `Added by ${resource.addedByName}`}
            </p>
          </div>
        </button>
        {canEdit && !isDefaultAgent && (
          <button
            onClick={() => onRemove(resource.uuid)}
            className="p-1.5 text-content-quaternary hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
            title="Remove from project"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Resource Modal — attach an existing agent or data source to the project
// ---------------------------------------------------------------------------

type AddableResourceType = "agent" | "data_collection";

/** Normalized shape both Agents and DataCollections are mapped into, so the
 * modal's render logic never has to branch on resource type. */
interface AddResourceOption {
  uuid: string;
  name: string;
  subtitle: string;
  icon: ReactNode;
}

function toAgentOption(agent: Agent): AddResourceOption {
  return {
    uuid: agent.uuid,
    name: agent.name,
    subtitle: agent.description ?? "",
    icon: (
      <div
        className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${agent.color || "#7C16F5"}20` }}
      >
        <Bot className="h-4 w-4" style={{ color: agent.color || "#7C16F5" }} />
      </div>
    ),
  };
}

function toDataSourceOption(ds: DataCollection): AddResourceOption {
  return {
    uuid: ds.uuid,
    name: ds.name,
    subtitle: ds.sourceType?.replace("_", " ") ?? "",
    icon: (
      <div className="h-8 w-8 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
        <Database className="h-4 w-4 text-emerald-500" />
      </div>
    ),
  };
}

/** Fetches and normalizes the pickable options for a given resource type. */
async function fetchResourceOptions(resourceType: AddableResourceType): Promise<AddResourceOption[]> {
  if (resourceType === "agent") {
    const res = await agentService.list({ pageSize: 100 });
    return res.data.map(toAgentOption);
  }
  const res = await dataSourceService.list({ pageSize: 100 });
  return res.data.map(toDataSourceOption);
}

/** Builds the POST /projects/{uuid}/resources payload for the selected item. */
async function submitResource(
  projectId: string,
  resourceType: AddableResourceType,
  selectedUuid: string,
): Promise<void> {
  await projectService.addResource(projectId, {
    resourceType,
    ...(resourceType === "agent" ? { agentUuid: selectedUuid } : { dataCollectionUuid: selectedUuid }),
  });
}

/** Creates a new agent or data source and attaches it to the project. */
async function createAndAttachResource(
  projectId: string,
  resourceType: AddableResourceType,
  name: string,
  opts: { prompt?: string; teamUuid: string; modelId: string },
): Promise<void> {
  let createdUuid: string;

  if (resourceType === "agent") {
    const res = await agentService.create({
      name,
      systemPrompt: opts.prompt || `You are ${name}, a helpful AI assistant.`,
      modelId: opts.modelId,
      teamUuid: opts.teamUuid,
    });
    createdUuid = res.data.uuid;
  } else {
    const res = await dataSourceService.create({
      name,
      sourceType: "upload",
      teamUuid: opts.teamUuid,
      visibility: "team",
    } as Partial<DataCollection> & { teamUuid: string; visibility: string });
    createdUuid = res.data.uuid;
  }

  await submitResource(projectId, resourceType, createdUuid);
}

function AddResourceModal({
  resourceType,
  projectId,
  existingUuids,
  onClose,
  onAdded,
}: Readonly<{
  resourceType: AddableResourceType;
  projectId: string;
  existingUuids: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}>) {
  const { showToast } = useToast();
  const { teams, enabledModels, tierDefaults } = useApp();
  const isAgent = resourceType === "agent";
  const title = isAgent ? "Add Agent" : "Add Data Source";
  const noun = isAgent ? "agent" : "data source";
  const nounPlural = isAgent ? "agents" : "data sources";

  const [mode, setMode] = useState<"pick" | "create">("pick");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [query, setQuery] = useState("");
  const [allOptions, setAllOptions] = useState<AddResourceOption[]>([]);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  // Create-mode form state
  const defaultTeamUuid = (teams.find((t) => t.isEveryoneTeam) || teams[0])?.uuid || "";
  const defaultModelId = tierDefaults.some((t) => t.tier === "thorough")
    ? "tier:thorough"
    : enabledModels[0]?.id || "model-router";
  const [createName, setCreateName] = useState("");
  const [createPrompt, setCreatePrompt] = useState("");

  useEffect(() => {
    if (mode !== "pick") return;
    let cancelled = false;
    setLoading(true);
    fetchResourceOptions(resourceType)
      .then((options) => {
        if (!cancelled) setAllOptions(options);
      })
      .catch((err) => {
        console.error(`Failed to load ${resourceType} options:`, err);
        if (!cancelled) showToast(`Failed to load ${nounPlural}`, "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceType, mode]);

  const lowerQuery = query.trim().toLowerCase();
  const options = allOptions.filter((o) => !existingUuids.has(o.uuid) && o.name.toLowerCase().includes(lowerQuery));

  const handlePickSubmit = async () => {
    if (!selectedUuid) return;
    try {
      setSubmitting(true);
      await submitResource(projectId, resourceType, selectedUuid);
      showToast(`${isAgent ? "Agent" : "Data source"} added`, "success");
      onAdded();
      onClose();
    } catch (err: unknown) {
      console.error(`Failed to add ${resourceType}:`, err);
      const apiErr = err as { data?: { detail?: string } };
      showToast(apiErr?.data?.detail || `Failed to add ${noun}`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateSubmit = async () => {
    if (!createName.trim()) return;
    try {
      setSubmitting(true);
      await createAndAttachResource(projectId, resourceType, createName.trim(), {
        prompt: createPrompt.trim() || undefined,
        teamUuid: defaultTeamUuid,
        modelId: defaultModelId,
      });
      showToast(`${isAgent ? "Agent" : "Data source"} created and added`, "success");
      onAdded();
      onClose();
    } catch (err: unknown) {
      console.error(`Failed to create ${resourceType}:`, err);
      const apiErr = err as { data?: { detail?: string } };
      showToast(apiErr?.data?.detail || `Failed to create ${noun}`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border-primary shrink-0">
          <h2 className="text-lg font-semibold text-content-primary">{title}</h2>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-1 text-content-tertiary hover:text-content-primary transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex border-b border-border-primary">
          <button
            type="button"
            onClick={() => setMode("pick")}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition border-b-2 ${
              mode === "pick"
                ? "border-brand-primary text-content-primary"
                : "border-transparent text-content-tertiary hover:text-content-primary"
            }`}
          >
            Pick Existing
          </button>
          <button
            type="button"
            onClick={() => setMode("create")}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition border-b-2 ${
              mode === "create"
                ? "border-brand-primary text-content-primary"
                : "border-transparent text-content-tertiary hover:text-content-primary"
            }`}
          >
            Create New
          </button>
        </div>

        {mode === "pick" ? (
          <>
            <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0">
              {isAgent && (
                <div className="flex items-start gap-2 p-3 bg-surface-secondary border border-border-primary rounded-lg text-xs text-content-tertiary">
                  <Info className="h-4 w-4 shrink-0 mt-0.5 text-content-quaternary" />
                  <span>
                    Adding an agent here doesn't grant access to it. Project members can only use agents their own
                    team/visibility permissions already allow.
                  </span>
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
                <input
                  type="text"
                  autoFocus
                  placeholder={`Search ${nounPlural}...`}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                />
              </div>
              <AddResourceOptionList
                loading={loading}
                options={options}
                query={query}
                nounPlural={nounPlural}
                selectedUuid={selectedUuid}
                onSelect={setSelectedUuid}
              />
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary shrink-0">
              <button
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePickSubmit}
                disabled={submitting || !selectedUuid}
                className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    Adding...
                  </>
                ) : (
                  title
                )}
              </button>
            </div>
          </>
        ) : (
          <CreateResourceForm
            isAgent={isAgent}
            submitting={submitting}
            createName={createName}
            setCreateName={setCreateName}
            createPrompt={createPrompt}
            setCreatePrompt={setCreatePrompt}
            onSubmit={handleCreateSubmit}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

/** The "Create New" form body, extracted to keep AddResourceModal's complexity low. */
function CreateResourceForm({
  isAgent,
  submitting,
  createName,
  setCreateName,
  createPrompt,
  setCreatePrompt,
  onSubmit,
  onClose,
}: Readonly<{
  isAgent: boolean;
  submitting: boolean;
  createName: string;
  setCreateName: (v: string) => void;
  createPrompt: string;
  setCreatePrompt: (v: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}>) {
  return (
    <>
      <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
        <div>
          <label htmlFor="create-resource-name" className="block text-sm font-medium text-content-secondary mb-1.5">
            Name <span className="text-red-400">*</span>
          </label>
          <input
            id="create-resource-name"
            type="text"
            autoFocus
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            placeholder={isAgent ? "e.g., Research Assistant" : "e.g., Project Documents"}
            className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
          />
        </div>
        {isAgent && (
          <div>
            <label htmlFor="create-resource-prompt" className="block text-sm font-medium text-content-secondary mb-1.5">
              System Prompt
            </label>
            <textarea
              id="create-resource-prompt"
              value={createPrompt}
              onChange={(e) => setCreatePrompt(e.target.value)}
              placeholder="You are a helpful assistant that..."
              rows={4}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none"
            />
            <p className="text-xs text-content-quaternary mt-1">
              Leave blank for a sensible default. You can customize later from the Agents page.
            </p>
          </div>
        )}
        {!isAgent && (
          <p className="text-xs text-content-quaternary">
            Creates an empty file-upload data source. You can upload files to it from the project&apos;s Knowledge Base
            panel or the Data Sources page.
          </p>
        )}
      </div>
      <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary shrink-0">
        <button
          onClick={onClose}
          disabled={submitting}
          className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onSubmit}
          disabled={submitting || !createName.trim()}
          className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
              Creating...
            </>
          ) : (
            "Create & Add"
          )}
        </button>
      </div>
    </>
  );
}

/** The search-results body of the Add Resource modal: loading / empty / list. */
function AddResourceOptionList({
  loading,
  options,
  query,
  nounPlural,
  selectedUuid,
  onSelect,
}: Readonly<{
  loading: boolean;
  options: AddResourceOption[];
  query: string;
  nounPlural: string;
  selectedUuid: string | null;
  onSelect: (uuid: string) => void;
}>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <p className="text-sm text-content-tertiary text-center py-10">
        {query.trim()
          ? "No matches found."
          : `No ${nounPlural} available to add. You may not have access to any, or they're all already attached.`}
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      {options.map((option) => (
        <ResourceOptionButton
          key={option.uuid}
          option={option}
          isSelected={selectedUuid === option.uuid}
          onSelect={() => onSelect(option.uuid)}
        />
      ))}
    </div>
  );
}

function ResourceOptionButton({
  option,
  isSelected,
  onSelect,
}: Readonly<{
  option: AddResourceOption;
  isSelected: boolean;
  onSelect: () => void;
}>) {
  return (
    <button
      onClick={onSelect}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors text-left ${
        isSelected
          ? "border-brand-primary bg-brand-primary/10"
          : "border-border-secondary hover:border-border-hover bg-surface-secondary"
      }`}
    >
      {option.icon}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-content-primary truncate">{option.name}</p>
        <p className="text-xs text-content-quaternary truncate capitalize">{option.subtitle}</p>
      </div>
    </button>
  );
}
